import { z } from 'zod'
import type { SubmissionFileWithUploaderDto } from './file'
import { publicIdSchema, utcTimestampSchema } from './primitives'

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/**
 * `draft -> ready -> submitted -> locked`, plus the `withdrawn`/
 * `disqualified` branches reachable from `submitted`/`locked`. The legal
 * transitions themselves live in `apps/api/src/services/submission-service.ts`
 * — this enum only says which strings are valid, not which moves between them
 * are allowed.
 */
export const SUBMISSION_STATUS_VALUES = [
  'draft',
  'ready',
  'submitted',
  'locked',
  'withdrawn',
  'disqualified',
] as const
export const submissionStatusSchema = z.enum(SUBMISSION_STATUS_VALUES)
export type SubmissionStatus = z.infer<typeof submissionStatusSchema>

// ---------------------------------------------------------------------------
// Submission
// ---------------------------------------------------------------------------

export const submissionSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  teamId: publicIdSchema,
  categoryId: publicIdSchema,
  roundId: publicIdSchema,
  formVersionId: publicIdSchema,
  /** Internal CAS generation; omitted from public DTOs. */
  draftRevision: z.int().min(0),
  status: submissionStatusSchema,
  finalVersionId: publicIdSchema.nullable(),
  submittedAt: utcTimestampSchema.nullable(),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type Submission = z.infer<typeof submissionSchema>

export const submissionDtoSchema = submissionSchema.omit({
  competitionId: true,
  draftRevision: true,
})
export type SubmissionDto = z.infer<typeof submissionDtoSchema>

/**
 * PR-260/PR-261/UX-257 follow-up (Student Journey Completion Wave SJ-5, API
 * delta): `GET .../submissions` (the caller's own team's full list, "/me"
 * from the client's point of view) is the one submission read every
 * multi-competition surface (`derive-home.ts`'s Home actions,
 * `derive-student-cards.ts`'s "我的競賽" cards) already fans out across many
 * competitions via `mySubmissionsQueryOptions`/`useMySubmissions` — adding a
 * second per-competition round-context fetch there would be an N+1 query for
 * every one of those surfaces. `isFinalRound` is computed once, server-side,
 * from the same round-order rule `student-competition-page.tsx` already uses
 * (`roundOrder > 1`; a competition has at most a preliminary + one final
 * round — see `rounds.ts`'s `key` CHECK), so those surfaces can finally pass
 * real round context into `deriveStudentJourneyStage` instead of a hardcoded
 * `false`. Additive-only: every other submission-returning endpoint keeps
 * returning the plain `SubmissionDto`.
 */
export const submissionListItemDtoSchema = submissionDtoSchema.extend({
  isFinalRound: z.boolean(),
})
export type SubmissionListItemDto = z.infer<typeof submissionListItemDtoSchema>

/** Admin/staff cross-team list view — adds the display names a raw id can't carry. */
export const adminSubmissionDtoSchema = submissionDtoSchema.extend({
  teamName: z.string(),
  categoryName: z.string(),
  roundName: z.string(),
})
export type AdminSubmissionDto = z.infer<typeof adminSubmissionDtoSchema>

/** Query params for the admin submissions list — offset-paginated, same shape as `teamListQuerySchema`. */
export const submissionListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  status: submissionStatusSchema.optional(),
  categoryId: publicIdSchema.optional(),
  teamId: publicIdSchema.optional(),
  roundId: publicIdSchema.optional(),
})
export type SubmissionListQuery = z.infer<typeof submissionListQuerySchema>

export const createSubmissionRequestSchema = z.strictObject({
  categoryId: publicIdSchema,
  roundId: publicIdSchema,
})
export type CreateSubmissionRequest = z.infer<typeof createSubmissionRequestSchema>

/** Marks a draft `ready` for submission, or moves it back to `draft` for further edits. */
export const updateSubmissionStatusRequestSchema = z.strictObject({
  status: z.enum(['draft', 'ready']),
})
export type UpdateSubmissionStatusRequest = z.infer<typeof updateSubmissionStatusRequestSchema>

/** The submit call itself carries no body — everything it needs is already on the submission. */
export const submitSubmissionRequestSchema = z.strictObject({})
export type SubmitSubmissionRequest = z.infer<typeof submitSubmissionRequestSchema>

/** Admin-only. A reason is mandatory — see `AUDIT_ACTIONS_REQUIRING_REASON`. */
export const lockSubmissionRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(2000),
})
export type LockSubmissionRequest = z.infer<typeof lockSubmissionRequestSchema>

/** Admin-only. A reason is mandatory — see `AUDIT_ACTIONS_REQUIRING_REASON`. */
export const unlockSubmissionRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(2000),
})
export type UnlockSubmissionRequest = z.infer<typeof unlockSubmissionRequestSchema>

export const withdrawSubmissionRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(2000),
})
export type WithdrawSubmissionRequest = z.infer<typeof withdrawSubmissionRequestSchema>

export const disqualifySubmissionRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(2000),
})
export type DisqualifySubmissionRequest = z.infer<typeof disqualifySubmissionRequestSchema>

/** PR-318: reinstates a disqualified submission (and with it the team). The reason is audit-only. */
export const reinstateSubmissionRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(2000),
})
export type ReinstateSubmissionRequest = z.infer<typeof reinstateSubmissionRequestSchema>

/** Admin-only. Reopens a submitted/locked submission back to `draft` for the team to fix and resubmit. */
export const returnForCorrectionRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(2000),
})
export type ReturnForCorrectionRequest = z.infer<typeof returnForCorrectionRequestSchema>

// ---------------------------------------------------------------------------
// Submission version
// ---------------------------------------------------------------------------

export const submissionVersionSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  submissionId: publicIdSchema,
  versionNumber: z.int().min(1),
  formVersionId: publicIdSchema,
  sourceDraftRevision: z.int().min(0),
  schemaSnapshot: z.unknown(),
  createdAt: utcTimestampSchema,
})
export type SubmissionVersion = z.infer<typeof submissionVersionSchema>

export const submissionVersionDtoSchema = submissionVersionSchema.omit({
  competitionId: true,
  submissionId: true,
  sourceDraftRevision: true,
})
export type SubmissionVersionDto = z.infer<typeof submissionVersionDtoSchema>

// ---------------------------------------------------------------------------
// Submission answer
// ---------------------------------------------------------------------------

export const submissionAnswerSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  submissionId: publicIdSchema,
  /** `null` = the current, mutable draft. Non-null = frozen at that version, forever. */
  submissionVersionId: publicIdSchema.nullable(),
  formFieldId: publicIdSchema,
  value: z.unknown(),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type SubmissionAnswer = z.infer<typeof submissionAnswerSchema>

/**
 * Keyed by field `key` (not `formFieldId`) — the same shape the Phase 3 form
 * renderer and `POST .../forms/:formId/versions/:versionId/validate` already
 * use, so answers round-trip through visibility/validation without a
 * translation layer on the client. The service layer maps key <-> field id
 * when it persists to `submission_answers`.
 */
export const submissionAnswersDtoSchema = z.record(z.string(), z.unknown())
export type SubmissionAnswersDto = z.infer<typeof submissionAnswersDtoSchema>

/**
 * Autosave body. Partial by design — a debounced client sends only the
 * fields that changed since the last save, not the whole form every time.
 */
export const updateSubmissionAnswersRequestSchema = z.strictObject({
  answers: z.record(z.string(), z.unknown()),
})
export type UpdateSubmissionAnswersRequest = z.infer<typeof updateSubmissionAnswersRequestSchema>

/**
 * PR-148/AC-153. `teamName`/`files` are resolved server-side for every
 * caller this endpoint serves (own-team member, or admin/staff with
 * `submission.read.all`) — a caller can already see their own team's name
 * and who on their team uploaded, so this is not a new disclosure, just
 * exposing data the row already carries (`submission_files.uploaded_by`,
 * `teams.name`). Judges never reach this DTO: `loadSubmissionWithAccess`
 * grants no judge branch, unlike the file-download authorization matrix.
 */
/**
 * PR-258 correction-reason API delta (Student Journey Completion Wave
 * SJ-5): `null` unless the submission is currently `draft`/`ready` AND has
 * actually been returned for correction at least once (the latest
 * `SUBMISSION_RETURN_FOR_CORRECTION` audit entry for this submission) —
 * never fabricated, and never populated for an ordinary never-submitted
 * draft. `requestedAt` is the audit entry's own timestamp, not guessed.
 */
export interface SubmissionCorrectionInfo {
  reason: string | null
  requestedAt: string
}

export interface SubmissionDetailDto {
  submission: SubmissionDto
  teamName: string
  answers: SubmissionAnswersDto
  files: SubmissionFileWithUploaderDto[]
  correction: SubmissionCorrectionInfo | null
}

// ---------------------------------------------------------------------------
// Submission receipt (PR-127/DM-036/DATA-042)
// ---------------------------------------------------------------------------

/** One entry per frozen file in the version this receipt documents. */
export const submissionReceiptManifestEntrySchema = z.object({
  fileId: publicIdSchema,
  filename: z.string().min(1),
  sizeBytes: z.int().min(0),
  checksumSha256: z.string().nullable(),
})
export type SubmissionReceiptManifestEntry = z.infer<typeof submissionReceiptManifestEntrySchema>

/**
 * `active` mirrors the submission's status at receipt-issue time; it is
 * updated (never re-created — the receipt row itself never changes shape)
 * when the underlying submission is later withdrawn or disqualified, per
 * FR-12's "若提交被正式撤銷，收據顯示對應狀態". It is never set back to
 * `active` from either terminal state.
 */
export const SUBMISSION_RECEIPT_STATUS_VALUES = ['active', 'withdrawn', 'disqualified'] as const
export const submissionReceiptStatusSchema = z.enum(SUBMISSION_RECEIPT_STATUS_VALUES)
export type SubmissionReceiptStatus = z.infer<typeof submissionReceiptStatusSchema>

export const submissionReceiptSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  submissionId: publicIdSchema,
  submissionVersionId: publicIdSchema,
  teamId: publicIdSchema,
  roundId: publicIdSchema,
  competitionTimezone: z.string().min(1),
  manifest: z.array(submissionReceiptManifestEntrySchema),
  submittedAt: utcTimestampSchema,
  status: submissionReceiptStatusSchema,
  createdAt: utcTimestampSchema,
})
export type SubmissionReceipt = z.infer<typeof submissionReceiptSchema>

/** Never carries `teamId`/`roundId` raw ids — the DTO layer resolves display names for those. */
export const submissionReceiptDtoSchema = submissionReceiptSchema
  .omit({ competitionId: true, teamId: true, roundId: true })
  .extend({
    teamName: z.string(),
    roundName: z.string(),
    competitionName: z.string(),
  })
export type SubmissionReceiptDto = z.infer<typeof submissionReceiptDtoSchema>
